import * as React from 'react';

import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { getKeyForExplorableItem } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { CardPositionContext, ContentInfoContext } from '../../contexts/TelemetryContexts';
import { ItemsSet } from '../../ItemsSet';
import { SpaceInfo } from '../../state/SpaceInfo';
import { CardScroller } from './CardScroller';
import {
  CardViewStyles, TOTAL_CARD_HEIGHT, TOTAL_CARD_WIDTH,
} from './CardViewStyles';
import { ConnectedContentCardWrapper } from './ConnectedContentCardWrapper';
import { applyDataRoleHack } from './DataRoleHack';

export const SpaceBetweenCardsInPx = 18;
export const CardContainerHorizontalPadding = SpaceContainerStyles.GlobalSpaceHorizontalPadding - SpaceBetweenCardsInPx / 2;

export interface CardViewProps {
  readonly deselectItem: (item: IContentInfo) => void;
  readonly items: ReadonlyArray<IExplorableItemTypeInfo>;
  readonly requestProjectContentsMissingSlice: (start: number, end: number) => void;
  readonly selectItem: (item: IContentInfo) => void;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly contentSort: ReadonlyArray<Readonly<IOrderClause>>;
  readonly isBusyFetchingItems: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly onCardScrollerLayoutUpdated?: () => void;
  readonly showAsRecommendation: boolean;
  readonly showActionMenu: boolean;
  readonly showSelection: boolean;
}

export class CardView extends React.Component<CardViewProps> {

  public render() {
    if (this.props.isBusyFetchingItems) {
      // If the initial render (which doesn't call shouldComponentUpdate) is still busy fetching items
      // Don't render anything.
      return null;
    }
    // TODO: Remove this nasty hack when data-role team fixes TFSID 903843
    for  (let i in this.props.items){
      applyDataRoleHack<IExplorableItemTypeInfo>(this.props.items[i]);
    }

    return <CardScroller items={this.props.items}
                         requestMissingSlice={this.props.requestProjectContentsMissingSlice}
                         renderFetchedCard={this.renderCard}
                         renderUnfetchedCard={UnfetchedCard}
                         getKeyForItem={getKeyForExplorableItem}
                         itemHeightInPx={TOTAL_CARD_HEIGHT}
                         itemWidthInPx={TOTAL_CARD_WIDTH}
                         itemSpacingInPx={SpaceBetweenCardsInPx}
                         sectionHorizontalPaddingInPx={CardContainerHorizontalPadding}
                         contentSort={this.props.contentSort}
                         onLayoutUpdated={this.props.onCardScrollerLayoutUpdated}
    />;
  }

  private renderCard = (props: { item: IContentInfo, index: number }): JSX.Element => {
    const { item, index } = props;
    return (
      <ContentInfoContext.Provider value={item}>
        <CardPositionContext.Provider value={index + 1}>
          <ConnectedContentCardWrapper
            content={item}
            toggleSelection={this.props.showSelection && this.toggleSelection}
            selected={this.props.showSelection && this.isItemSelected(item)}
            showActionMenu={this.props.showActionMenu}
            showAsRecommendation={this.props.showAsRecommendation}
          />
        </CardPositionContext.Provider>
      </ContentInfoContext.Provider>
    );
  }

  // When filtering on content type, the content type property will cause a rerender
  // however we won't have the updated items yet so do not want to rerender
  // So only rerender if we are told we aren't busy fetching new items.
  // Because Explorer (parent) is a pure component, we won't re render if items haven't changed
  public shouldComponentUpdate(nextProps: CardViewProps): boolean {
    return !nextProps.isBusyFetchingItems;
  }

  private isItemSelected = (content: IContentInfo): boolean => {
    const key = getKeyForExplorableItem(content);
    return !!this.props.selectedItems[key];
  }

  private toggleSelection = (content: IContentInfo): void => {
    this.isItemSelected(content) ? this.props.deselectItem(content) : this.props.selectItem(content);
  }
}

function UnfetchedCard() {
  return (
    <div className={`tb-unfetched-card ${CardViewStyles.Card}`}>
      <div className={CardViewStyles.Thumbnail} />
    </div>
  );
}
